import { useEffect, useState } from 'react'

import { Send, X } from 'lucide-react'
import { useDebounceValue } from 'usehooks-ts'

import { getT, getTMinus, toISO8601 } from '@fortem-monorepo/js-utilities'
import { useResizableState } from '@fortem-monorepo/react-hooks'
import { FiltersProvider, useFilters } from '@fortem-monorepo/react-spa'
import { createFilters } from '@fortem-monorepo/react-spa'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { DateRangePicker } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { Input } from '@fortem-monorepo/react-ui'
import { InputGroup, InputGroupAddon, InputGroupButton } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@fortem-monorepo/react-ui'
import { Trades } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { Page, PageProps } from '@/components/Page'
import { SendBespokeInvoiceModal } from '@/components/dialogs/SendBespokeInvoiceModal'
import { TRADING } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import { PanelToggle } from '@/pages/operations/mappings/page'
import { BespokeTradesGrid } from '@/pages/trading/booked-trades/grids/bespoke/grid'
import { NewdbTradesGrid } from '@/pages/trading/booked-trades/grids/newdb/grid'
import { OlddbTradesGrid } from '@/pages/trading/booked-trades/grids/olddb/grid'
import { useTradeStrategiesQuery } from '@/react-query/queries/static'

const filters = createFilters([
  {
    type: 'daterange',
    name: 'dates',
    defaultValue: {
      st_dt: getTMinus(5),
      ed_dt: getT(),
    },
  },
  {
    type: 'select',
    name: 'fund_name',
  },
  {
    type: 'text',
    name: 'unique_identifier',
  },
])

export interface BookedTradesPageProps {}

export const BookedTradesPage: React.FC<BookedTradesPageProps> = ({}) => {
  return (
    <Page metadata={{ title: 'Trades' }} permissions={TRADING.TRADES.permissions}>
      <FiltersProvider filters={filters}>
        <Content />
      </FiltersProvider>
    </Page>
  )
}

const Content: React.FC<PageProps> = ({}) => {
  const { registerFilter, values, setValue } = useFilters<typeof filters>()
  const [newDb, oldDb] = useResizableState(2)

  const [selectedTab, setSelectedTab] = useState<'funds' | 'bespoke'>('funds')

  const [unique_identifier] = useDebounceValue(values.unique_identifier, 500)

  const params = Object.fromEntries(
    Object.entries({
      st_dt: toISO8601(values.dates?.st_dt!),
      ed_dt: toISO8601(values.dates?.ed_dt ?? values.dates?.st_dt!),
      fund_name: values.fund_name,
      unique_identifier,
    } satisfies Trades.GetTradesParams).filter(([_, value]) => !!value),
  ) as Trades.GetTradesParams

  const strategies = useTradeStrategiesQuery()

  const strategyOptions = (strategies.data ?? []).filter((s) => {
    if (selectedTab === 'funds') {
      return s.strategy_type === 'FUND'
    }

    if (selectedTab === 'bespoke') {
      return s.strategy_type === 'BESPOKE'
    }

    return s
  })

  const [source, setSource] = useState<'Smartrade' | 'Manual' | 'all'>('all')
  const [isSendingInvoice, setSendingInvoice] = useState(false)

  useEffect(() => {
    setValue('fund_name', undefined)
  }, [selectedTab])

  return (
    <>
      <SendBespokeInvoiceModal open={isSendingInvoice} onOpenChange={setSendingInvoice} />
      <Tabs
        value={selectedTab}
        onValueChange={(val) => setSelectedTab(val as typeof selectedTab)}
        className={'gap-0 h-full'}
      >
        <HeaderConfigBar>
          <div className={'flex gap-2 items-center'}>
            <DateRangePicker {...registerFilter('dates')} size="sm" />
            <SearchSelect
              {...registerFilter('fund_name')}
              showNone={'All Strategies'}
              size="sm"
              placeholder={'Strategy'}
              loading={strategies.isFetching}
              className={'w-40 min-w-auto'}
              options={strategyOptions?.map((s) => ({
                label: s.strategy_name,
                value: s.strategy_name,
              }))}
            />
            <InputGroup size={'sm'}>
              <Input
                inputSize="sm"
                className={
                  'flex-1 rounded-none border-0 bg-transparent shadow-none focus-visible:ring-0 dark:bg-transparent'
                }
                value={values.unique_identifier}
                onChange={(e) => setValue('unique_identifier', e.target.value)}
                placeholder={'Unique Identifier'}
              />
              <InputGroupAddon>
                <InputGroupButton
                  onClick={() => {
                    setValue('unique_identifier', '')
                  }}
                >
                  <X />
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>
          </div>
        </HeaderConfigBar>
        <HeaderConfigBar className={'flex items-center gap-4'}>
          <TabsList>
            <TabsTrigger value="funds">Funds</TabsTrigger>
            <TabsTrigger value="bespoke">Bespoke</TabsTrigger>
          </TabsList>
          {selectedTab === 'funds' && (
            <>
              <PanelToggle
                open={newDb.open}
                onClick={() => newDb.toggle()}
                className={'border-r rounded-none'}
              >
                New DB
              </PanelToggle>
              <PanelToggle
                open={oldDb.open}
                onClick={() => oldDb.toggle()}
                className={'border-r rounded-none'}
              >
                Old DB
              </PanelToggle>
            </>
          )}
          {selectedTab === 'bespoke' && (
            <>
              <Tabs value={source} onValueChange={(val) => setSource(val as typeof source)}>
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="Manual">Manual</TabsTrigger>
                  <TabsTrigger value="Smartrade">Smartrade</TabsTrigger>
                </TabsList>
              </Tabs>

              <CanAccess permissions={[PERMISSIONS.SEND_BESPOKE_INVOICE]}>
                <Button size="sm" variant={'outline'} onClick={() => setSendingInvoice(true)}>
                  Send Invoice
                  <Send />
                </Button>
              </CanAccess>
            </>
          )}
        </HeaderConfigBar>
        <TabsContent value="funds">
          <ResizablePanelGroup direction={'horizontal'} autoSaveId={'conditional'}>
            {newDb.open && (
              <>
                <ResizablePanel id={'trades-newdb'}>
                  <NewdbTradesGrid params={params} />
                </ResizablePanel>
                <ResizableHandle />
              </>
            )}
            {oldDb.open && (
              <ResizablePanel id={'trades-olddb'}>
                <OlddbTradesGrid params={params} />
              </ResizablePanel>
            )}
          </ResizablePanelGroup>
        </TabsContent>
        <TabsContent value="bespoke">
          <BespokeTradesGrid params={params} source={source} />
        </TabsContent>
      </Tabs>
    </>
  )
}
